/**
 * Pinned (sticky) table columns — the one place the class strings are spelled.
 *
 * Policy (ratified 2026-08-20): a row's three-dot **actions** column is pinned to
 * the inline end at EVERY viewport, and a table's **lead** columns (selection
 * checkbox + identity cell) are pinned to the inline start on desktop only. A
 * horizontally scrolling table must never push its row menu off screen — the
 * report behind this was the Tasks board on a phone, where reaching the menu meant
 * scrolling past six columns.
 *
 * `DataTable` applies these by default; the handful of hand-rolled `<Table>`s that
 * deliberately bypass `DataTable` (drag-and-drop rows, inline editing) import them
 * so the affordance looks identical wherever it appears.
 *
 * Three things the constants carry that a call site keeps getting wrong:
 *
 * - **Logical insets** (`start-0` / `end-0`), not `left-0` / `right-0` — the app
 *   sets `dir="rtl"` globally for Hebrew and Arabic, so a physical inset pins the
 *   wrong edge in half the languages we ship. The separating shadow has no logical
 *   form, so it carries an explicit `rtl:` counterpart.
 * - **An opaque backdrop.** A sticky cell with a transparent background lets the
 *   scrolled-under content read straight through it.
 * - **The row's own tint.** `bg-background` alone leaves the pinned column the one
 *   stripe that does not light up on hover or when the row is selected, which reads
 *   as a rendering fault. Rows that want this carry `group/row` (DataTable's do).
 */

/** Opaque backdrop for a pinned cell, tracking its row's hover/selected tint. */
export const STICKY_CELL_SURFACE =
  'bg-background group-hover/row:bg-muted/50 group-data-[state=selected]/row:bg-muted/50';

/**
 * An OPAQUE `bg-muted/30`, for a pinned cell on a row whose tint is translucent.
 *
 * A row's own background paints behind its cells, so an untinted pinned cell on a
 * tinted row reads as a rendering fault — but simply repeating `bg-muted/30` on the
 * cell would leave it 70% transparent, and what slides under a pinned cell is the
 * text of its own row's other cells. This composites the same tint over
 * `--background` in one paint, so it matches the row exactly AND hides what scrolls
 * beneath. Used by the tasks tables, whose sub-item rows carry a depth tint.
 */
export const STICKY_CELL_SURFACE_SUBTLE =
  '[background:linear-gradient(hsl(var(--muted)/0.3),hsl(var(--muted)/0.3)),hsl(var(--background))]';

/** Pinned to the inline END (the actions column). */
export const STICKY_ACTIONS_CELL =
  'sticky end-0 z-10 shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)] rtl:shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]';

/** Pinned to the inline START (selection checkbox / identity cell). */
export const STICKY_LEAD_CELL =
  'sticky start-0 z-10 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)] rtl:shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)]';

/**
 * The same pin without `start-0`, for a lead cell that stops short of the edge
 * because a pinned sibling sits before it — pair with an inline
 * `insetInlineStart`. See `stickyIdentityOffset`.
 */
export const STICKY_LEAD_CELL_OFFSET =
  'sticky z-10 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)] rtl:shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)]';

/**
 * Width of `DataTable`'s selection-checkbox column (`w-[40px]`), which is where
 * the identity column has to come to rest when both are pinned.
 */
export const SELECTION_COLUMN_WIDTH_PX = 40;

/**
 * Inline offset for a pinned identity column: flush to the edge on a table with no
 * selection checkbox, one checkbox-width in when there is one. `insetInlineStart`
 * rather than `left` so it follows the document direction.
 */
export function stickyIdentityOffset(selectable: boolean): { insetInlineStart: number } {
  return { insetInlineStart: selectable ? SELECTION_COLUMN_WIDTH_PX : 0 };
}

/**
 * The opaque backdrop for a pinned cell on a HIERARCHY table — one whose rows
 * carry a selected tint and a depth tint of their own. Both tasks tables spell
 * exactly this, which is why it lives here rather than a third time in the next
 * hand-rolled table that adopts the pattern.
 *
 * `bg-muted` (selected) is already opaque; the depth tint is not, so it goes
 * through `STICKY_CELL_SURFACE_SUBTLE`. Hover is a named-group variant, so the
 * row needs `group/row`.
 */
export function stickyHierarchyRowSurface(isSelected: boolean, depth: number): string {
  const base = isSelected
    ? 'bg-muted'
    : depth > 0
      ? STICKY_CELL_SURFACE_SUBTLE
      : 'bg-background';
  return `${base} group-hover/row:bg-muted/50`;
}
